import { useEffect, useState } from 'react';
import { createRoute, stripSearchParams } from '@tanstack/react-router';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import { useLocationDetail } from '../../hooks/locations';
import {
  LOCATIONS_SEARCH_DEFAULTS,
  parseLocationsSearch,
} from '../../hooks/useLocationsRouterSearch';
import { TableViewSkeleton } from '../../components/Skeleton/TableViewSkeleton';
import { LocationEditSkeleton } from '../../components/Skeleton/LocationEditSkeleton';
import { ApiError } from '../../api/client';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { CAPABILITIES } from '../../lib/capabilities';
import { requireCapability } from '../requireCapability';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect (see below).
import type LocationManagement from '../../components/LocationManagement/index';
import type EditLocation from '../../components/EditLocation/index';

export const locationsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/locations',
  validateSearch: parseLocationsSearch,
  search: { middlewares: [stripSearchParams(LOCATIONS_SEARCH_DEFAULTS)] },
  component: function LocationsIndexRoute() {
    const { t } = useTranslation();
    const { currentUser } = useAppRouterInitial();
    useDocumentTitle(t('common.page_titles.locations'));
    const [Component, setComponent] = useState<typeof LocationManagement | null>(null);
    useEffect(() => {
      import('../../components/LocationManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('common.loading_locations')} />;
    }
    return <Component currentUser={currentUser} />;
  },
});

export const locationEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/locations/$id/edit',
  // RBAC: edit is a write-only surface. The guard redirects read-only users
  // (no locations.write) to the Locations index before the component runs. The
  // server policy (LocationsController#edit) enforces this too; this is UX.
  beforeLoad: requireCapability(CAPABILITIES.LOCATIONS_WRITE, { to: '/locations' }),
  component: function LocationEditRoute() {
    const params = locationEditRoute.useParams();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const { data, error } = useLocationDetail(idNum);
    useDocumentTitle(
      data?.location
        ? t('common.page_titles.edit_location', { postal_code: data.location.postal_code })
        : t('common.page_titles.locations'),
    );
    const [Component, setComponent] = useState<typeof EditLocation | null>(null);
    useEffect(() => {
      import('../../components/EditLocation/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    // The location is gone (deleted here or in another tab) — send the user
    // back to the list rather than spinning on the skeleton forever. The
    // 4xx-no-retry in useLocationDetail makes this settle fast.
    const isNotFound = error instanceof ApiError && error.status === 404;
    useEffect(() => {
      if (isNotFound) window.location.href = '/locations';
    }, [isNotFound]);

    const location = data?.location;
    if (!Component || !location) {
      return <LocationEditSkeleton label={t('common.loading_locations')} />;
    }
    return <Component id={idNum} initialLocation={location} />;
  },
});
